/**
 * SHOWICT Quantum — estadísticas del dashboard con budget DUAL.
 *
 * Vive fuera del adapter a propósito: el adapter es solo lectura del motor +
 * derivación de niveles, y este cálculo agregado (ventana, ratios, R medio,
 * racha, MAX DD, PnL estimado por grado) es propio de la presentación
 * Quantum. Criterio de ventana idéntico a PRQ/SuperTrend Quantum: últimas 50
 * señales o 90 días, lo que dé más muestra.
 *
 * Budget DUAL (dimensión extra sobre el molde PRQ): las estadísticas se
 * calculan por grado de señal (A+ = BOS, A = CHoCH) y en combinado, cada
 * grado con su propio budget (cfg.budgetAp / cfg.budgetA). El PnL combinado
 * es Σ(R_grado × budget_grado) sobre los grados con budget > 0.
 *
 * RACHA y MAX DD NO existen en el molde PRQ: son mejora propia de este
 * indicador (racha actual de cerradas cronológicas; drawdown máximo de la
 * curva acumulada de R de las cerradas cronológicas).
 */

const SIGNAL_WINDOW_DAYS = 90
const SIGNAL_WINDOW_COUNT = 50

// Familia de resolución por niveles (TP3/SL). Las invalidadas (señal contraria)
// NO son de esta familia: fuera de los denominadores TP%/SL% y del winrate —
// pero su R realizado SÍ entra en PnL, R medio, racha y MAX DD (son operaciones
// cerradas reales). Decisión de la ronda v1.2, documentada en la ficha.
const RESOLVED_OUTCOMES = new Set(['win', 'be', 'loss'])

// Tolerancia para el signo del R realizado (una invalidada con R≈0 resetea la racha).
const R_EPSILON = 1e-9

/**
 * R de una señal cerrada, por jerarquía de nivel alcanzado (anti-doble-
 * cómputo): TP3 > TP2 > TP1, cada uno a su R-múltiplo de config (defaults
 * +3/+2/+1); SL = −1. El outcome 'be' (SL tras TP1 con el stop ya en el
 * entry, semántica PRQ) cuenta por tp1Hit = +tp1 R, igual que PRQ. Las
 * señales abiertas están excluidas del cómputo (las filtra el caller).
 */
function signalR(sig, mults) {
  if (sig.outcome === 'loss') return -1
  if (sig.tp3Hit != null) return mults.tp3
  if (sig.tp2Hit != null) return mults.tp2
  if (sig.tp1Hit != null) return mults.tp1
  return 0
}

/** R realizado de una cerrada: jerarquía TP/SL o el realizedR de la invalidada. */
function realizedR(sig, mults) {
  if (sig.outcome === 'invalidada') {
    return Number.isFinite(sig.realizedR) ? sig.realizedR : 0
  }
  return signalR(sig, mults)
}

/**
 * Ventana PRQ: últimas 50 señales o 90 días, lo que dé más muestra.
 * Devuelve la ventana en orden cronológico.
 */
function applyWindow(list) {
  const cutoffTime = Date.now() / 1000 - SIGNAL_WINDOW_DAYS * 24 * 3600
  const sortedDesc = [...list].sort((a, b) => (a.time > b.time ? -1 : a.time < b.time ? 1 : 0))
  const windowed = []
  for (const sig of sortedDesc) {
    if (windowed.length >= SIGNAL_WINDOW_COUNT) break
    if (typeof sig.time === 'number' && sig.time < cutoffTime && windowed.length > 0) break
    windowed.push(sig)
  }
  return windowed.reverse()
}

/**
 * Estadísticas de un conjunto de señales (un grado o el combinado).
 * @param {Array} signals  señales ShowictSignal (orden cronológico)
 * @param {number|null} budget  budget por trade del grado; el PnL estimado
 *   solo se calcula si es un número > 0. Pasar null en el combinado (su PnL
 *   se compone fuera, por grado).
 * @param {{tp1:number, tp2:number, tp3:number}} mults  R-múltiplos de config
 */
function computeGradeStats(signals, budget, mults) {
  const empty = {
    totalSignals: 0, tp1Rate: 0, tp2Rate: 0, tp3Rate: 0, slRate: 0, openRate: 0, invalidatedRate: 0,
    winrate: 0, avgR: 0, totalR: 0, closedCount: 0, resolvedCount: 0, estimatedPnl: null,
    streak: { type: null, count: 0 }, maxDrawdown: 0, last10: [],
  }
  const list = Array.isArray(signals) ? signals : []
  if (list.length === 0) return empty

  const windowSignals = applyWindow(list)
  const total = windowSignals.length
  if (total === 0) return empty

  // Resueltas por niveles (familia TP3/SL): único denominador de TP%/SL% y
  // del winrate. Invalidadas y abiertas quedan FUERA de ese denominador.
  const resolved = windowSignals.filter(s => RESOLVED_OUTCOMES.has(s.outcome))
  const resolvedCount = resolved.length
  const invalidatedCount = windowSignals.filter(s => s.outcome === 'invalidada').length
  const openCount = windowSignals.filter(s => s.outcome === 'active').length

  const tp1Count = resolved.filter(s => s.tp1Hit != null).length
  const tp2Count = resolved.filter(s => s.tp2Hit != null).length
  const tp3Count = resolved.filter(s => s.tp3Hit != null).length
  const slCount = resolved.filter(s => s.outcome === 'loss').length

  // Cerradas en orden cronológico (la ventana ya viene cronológica): resueltas
  // + invalidadas — el R realizado de TODAS entra en PnL, R medio, racha y DD.
  const closed = windowSignals.filter(s => s.outcome !== 'active')
  const closedCount = closed.length
  // Winrate sobre resueltas TP/SL: gana la que alcanzó al menos TP1 (incluye
  // los BE: outcome 'be' con tp1Hit, semántica PRQ). Las invalidadas no lo diluyen.
  const winCount = resolved.filter(s => s.tp1Hit != null).length

  const rSum = closed.reduce((sum, s) => sum + realizedR(s, mults), 0)
  const hasBudget = Number.isFinite(budget) && budget > 0

  // Racha actual de cerradas cronológicas por SIGNO del R realizado
  // (R>0 → win, R<0 → loss; R≈0 resetea la racha).
  let streak = { type: null, count: 0 }
  for (let i = closed.length - 1; i >= 0; i--) {
    const r = realizedR(closed[i], mults)
    if (Math.abs(r) < R_EPSILON) break // R≈0: resetea
    const type = r > 0 ? 'win' : 'loss'
    if (streak.type === null) {
      streak = { type, count: 1 }
    } else if (type === streak.type) {
      streak = { ...streak, count: streak.count + 1 }
    } else {
      break
    }
  }

  // MAX DD: drawdown máximo (en R, ≥ 0) de la curva acumulada de R de las
  // cerradas cronológicas (con el R realizado de las invalidadas).
  let cum = 0
  let peak = 0
  let maxDrawdown = 0
  for (const s of closed) {
    cum += realizedR(s, mults)
    if (cum > peak) peak = cum
    const dd = peak - cum
    if (dd > maxDrawdown) maxDrawdown = dd
  }

  return {
    totalSignals: total,
    tp1Rate: resolvedCount > 0 ? tp1Count / resolvedCount : 0,
    tp2Rate: resolvedCount > 0 ? tp2Count / resolvedCount : 0,
    tp3Rate: resolvedCount > 0 ? tp3Count / resolvedCount : 0,
    slRate: resolvedCount > 0 ? slCount / resolvedCount : 0,
    openRate: openCount / total,
    invalidatedRate: invalidatedCount / total,
    winrate: resolvedCount > 0 ? winCount / resolvedCount : 0,
    avgR: closedCount > 0 ? rSum / closedCount : 0,
    totalR: rSum,
    closedCount,
    resolvedCount,
    estimatedPnl: hasBudget && closedCount > 0 ? rSum * budget : null,
    streak,
    maxDrawdown,
    last10: windowSignals.slice(-10),
  }
}

/**
 * Estadísticas del dashboard con budget dual.
 * @param {Array} signals  señales ShowictSignal (orden cronológico)
 * @param {Object} cfg     config ya resuelta por resolveConfig() (usa
 *   budgetAp, budgetA y tpMults)
 * @returns {import('./types.js').ShowictStats}
 */
export function computeStats(signals, cfg) {
  const list = Array.isArray(signals) ? signals : []
  const mults = cfg?.tpMults && typeof cfg.tpMults === 'object'
    ? cfg.tpMults
    : { tp1: 1, tp2: 2, tp3: 3 }

  const ap = computeGradeStats(list.filter(s => s.grade === 'A+'), cfg?.budgetAp, mults)
  const a = computeGradeStats(list.filter(s => s.grade === 'A'), cfg?.budgetA, mults)

  // Combinado: curva de R sumando ambos grados (orden cronológico global).
  // Su PnL estimado es Σ(R_grado × budget_grado) sobre los grados con
  // budget > 0; null si ninguno tiene budget o no hay cerradas.
  const combined = computeGradeStats(list, null, mults)
  let combinedPnl = null
  if (combined.closedCount > 0) {
    let sum = 0
    let any = false
    if (Number.isFinite(cfg?.budgetAp) && cfg.budgetAp > 0) { sum += ap.totalR * cfg.budgetAp; any = true }
    if (Number.isFinite(cfg?.budgetA) && cfg.budgetA > 0) { sum += a.totalR * cfg.budgetA; any = true }
    combinedPnl = any ? sum : null
  }
  combined.estimatedPnl = combinedPnl

  return { ap, a, combined }
}
